import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, Shield, Users, Lock, Activity, Eye, Trash2, Download, Ban, Unlock } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Security = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [eventsByType, setEventsByType] = useState([]);
  const [eventsBySeverity, setEventsBySeverity] = useState([]);
  const [blockedIps, setBlockedIps] = useState([]);
  const [unblockingIp, setUnblockingIp] = useState(null);
  const [mongoHealth, setMongoHealth] = useState(null);
  const [mongoLoading, setMongoLoading] = useState(false);

  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    setLoading(true);
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      const [statsRes, eventsRes, timeSeriesRes, blockedIpsRes] = await Promise.all([
        axios.get(`${API}/admin/security/stats`, config),
        axios.get(`${API}/admin/security/events`, config),
        axios.get(`${API}/admin/security/time-series`, config),
        axios.get(`${API}/admin/security/blocked-ips`, config)
      ]);

      // console.log('Stats response:', statsRes.data);
      // console.log('Events response:', eventsRes.data);
      // console.log('Time series response:', timeSeriesRes.data);
      // console.log('Blocked IPs response:', blockedIpsRes.data);
      
      setStats(statsRes.data);
      setEvents(eventsRes.data.events || []);
      setTimeSeriesData(timeSeriesRes.data.time_series || []);
      setEventsByType(timeSeriesRes.data.by_type || []);
      setEventsBySeverity(timeSeriesRes.data.by_severity || []);
      setBlockedIps(blockedIpsRes.data.blocked_ips || []);
    } catch (error) {
      console.error('Error loading security data:', error);
      console.error('Error details:', error.response?.data);
      // Set default values on error
      setStats({
        total_users: 0,
        mfa_enabled: 0,
        failed_logins_24h: 0,
        status: 'UNKNOWN',
        top_ips: []
      });
      setBlockedIps([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockIp = async (ipAddress) => {
    if (!window.confirm(`Are you sure you want to unblock IP address ${ipAddress}?`)) {
      return;
    }

    setUnblockingIp(ipAddress);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/security/unblock-ip`,
        null,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          params: {
            ip_address: ipAddress
          }
        }
      );

      // Remove from local state
      setBlockedIps(prev => prev.filter(ip => ip.ip_address !== ipAddress));
      
      // Show success message
      alert(`IP ${ipAddress} has been unblocked successfully`);
    } catch (error) {
      console.error('Error unblocking IP:', error);
      alert(`Failed to unblock IP: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUnblockingIp(null);
    }
  };

  const checkMongoHealth = async () => {
    setMongoLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/mongodb/health`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setMongoHealth(response.data);
    } catch (error) {
      console.error('Error checking MongoDB health:', error);
      setMongoHealth({ status: 'error', security_alerts: [`Failed to check: ${error.message}`] });
    } finally {
      setMongoLoading(false);
    }
  };

  const exportSecurityLog = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/security/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `security-log-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting security log:', error);
    }
  };

  const SEVERITY_COLORS = {
    critical: '#ef4444',
    high: '#f97316',
    medium: '#eab308',
    low: '#22c55e',
    info: '#3b82f6'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="h-12 w-12 animate-pulse text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading security dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => window.location.href = '/dashboard'}
              className="flex items-center gap-2"
            >
              ← Back to Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => window.location.href = '/admin'}
              className="flex items-center gap-2"
            >
              Admin
            </Button>
            <Button onClick={exportSecurityLog} className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Export Log
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
              <Shield className="h-8 w-8 text-blue-600" />
              Security Dashboard
            </h1>
            <p className="text-slate-600 mt-1">Monitor system security and user activity</p>
          </div>
        </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Users</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-slate-800">{stats?.total_users || 0}</div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Failed Logins (24h)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-orange-600">{stats?.failed_logins_24h || 0}</div>
              <AlertCircle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>MFA Enabled</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-slate-800">{stats?.mfa_enabled || 0}</div>
              <Lock className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>System Status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className={`text-2xl font-bold ${stats?.status === 'HEALTHY' ? 'text-green-600' : 'text-red-600'}`}>
                {stats?.status || 'UNKNOWN'}
              </div>
              <Activity className={`h-8 w-8 ${stats?.status === 'HEALTHY' ? 'text-green-600' : 'text-red-600'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Security Events Over Time */}
        <Card>
          <CardHeader>
            <CardTitle>Security Events Over Time</CardTitle>
            <CardDescription>Last 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Events by Type */}
        <Card>
          <CardHeader>
            <CardTitle>Events by Type</CardTitle>
            <CardDescription>Event distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={eventsByType}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Events By Severity and Top IP Addresses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Events by Severity */}
        <Card>
          <CardHeader>
            <CardTitle>Events by Severity</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={eventsBySeverity}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {eventsBySeverity.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={SEVERITY_COLORS[entry.severity] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top IP Addresses */}
        <Card>
          <CardHeader>
            <CardTitle>Top Related Addresses</CardTitle>
            <CardDescription>Most active IP addresses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.top_ips?.slice(0, 5).map((ip, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${ip.count > 10 ? 'bg-red-500' : ip.count > 5 ? 'bg-orange-500' : 'bg-green-500'}`}></div>
                    <span className="font-mono text-sm">{ip.ip_address}</span>
                  </div>
                  <span className="text-sm font-semibold text-slate-600">{ip.count} events</span>
                </div>
              )) || <p className="text-slate-500 text-sm">No data available</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Blocked IP Addresses */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Ban className="h-5 w-5 text-red-600" />
                Blocked IP Addresses
              </CardTitle>
              <CardDescription>IPs blocked by WAF for malicious activity</CardDescription>
            </div>
            <div className="text-2xl font-bold text-red-600">{blockedIps.length}</div>
          </div>
        </CardHeader>
        <CardContent>
          {blockedIps.length > 0 ? (
            <div className="space-y-3">
              {blockedIps.map((ip, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <Ban className="h-4 w-4 text-red-600" />
                      <span className="font-mono text-sm font-semibold text-slate-800">{ip.ip_address}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        ip.severity === 'critical' ? 'bg-red-100 text-red-700' :
                        ip.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {ip.severity}
                      </span>
                    </div>
                    <div className="text-sm text-slate-600">
                      <span className="font-medium">Reason:</span> {ip.reason}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Blocked: {new Date(ip.blocked_at).toLocaleString()}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUnblockIp(ip.ip_address)}
                    disabled={unblockingIp === ip.ip_address}
                    className="ml-4 flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-50"
                  >
                    <Unlock className="h-4 w-4" />
                    {unblockingIp === ip.ip_address ? 'Unblocking...' : 'Unblock'}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Ban className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No blocked IP addresses</p>
              <p className="text-xs mt-1">IPs will appear here when blocked by the WAF for malicious activity</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* MongoDB Security Monitoring */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-green-600" />
                Database Security (MongoDB)
              </CardTitle>
              <CardDescription>Monitor database health and security status (CVE-2025-14847 MongoBleed protection)</CardDescription>
            </div>
            <Button 
              onClick={checkMongoHealth} 
              disabled={mongoLoading}
              variant="outline"
              size="sm"
            >
              {mongoLoading ? 'Checking...' : 'Check Health'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {mongoHealth ? (
            <div className="space-y-4">
              {/* Status Banner */}
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                mongoHealth.status === 'healthy' ? 'bg-green-50 border border-green-200' :
                mongoHealth.status === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
                'bg-red-50 border border-red-200'
              }`}>
                <div className={`w-3 h-3 rounded-full ${
                  mongoHealth.status === 'healthy' ? 'bg-green-500' :
                  mongoHealth.status === 'warning' ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}></div>
                <span className={`font-medium ${
                  mongoHealth.status === 'healthy' ? 'text-green-700' :
                  mongoHealth.status === 'warning' ? 'text-yellow-700' :
                  'text-red-700'
                }`}>
                  MongoDB {mongoHealth.status?.toUpperCase()} - v{mongoHealth.version}
                </span>
              </div>

              {/* Security Alerts */}
              {mongoHealth.security_alerts?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <span className="font-medium text-red-700">Security Alerts</span>
                  </div>
                  <ul className="list-disc list-inside text-sm text-red-600">
                    {mongoHealth.security_alerts.map((alert, idx) => (
                      <li key={idx}>{alert}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Connections</div>
                  <div className="text-lg font-semibold">{mongoHealth.current_connections || 0}</div>
                  <div className="text-xs text-slate-400">of {mongoHealth.available_connections || 0} available</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Memory Usage</div>
                  <div className="text-lg font-semibold">{mongoHealth.memory_usage_mb || 0} MB</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Network In</div>
                  <div className="text-lg font-semibold">{((mongoHealth.network_bytes_in || 0) / 1024 / 1024).toFixed(1)} MB</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Network Out</div>
                  <div className="text-lg font-semibold">{((mongoHealth.network_bytes_out || 0) / 1024 / 1024).toFixed(1)} MB</div>
                </div>
              </div>

              {/* Compression Status */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-xs text-slate-500 mb-1">Compression (MongoBleed CVE-2025-14847)</div>
                <div className="flex items-center gap-2">
                  {mongoHealth.compression_enabled?.includes('zlib') ? (
                    <>
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      <span className="text-sm text-red-600 font-medium">Zlib ENABLED - Vulnerable</span>
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      <span className="text-sm text-green-600 font-medium">Zlib DISABLED - Protected</span>
                    </>
                  )}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Active compressors: {mongoHealth.compression_enabled?.join(', ') || 'none'}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Activity className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">Click "Check Health" to monitor MongoDB security status</p>
              <p className="text-xs mt-1">Monitors for MongoBleed vulnerability and unusual activity</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* External API Security Monitoring */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                External API Security
              </CardTitle>
              <CardDescription>Monitor unauthorized API access attempts (invalid/missing API keys)</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Filter for API-related events */}
            {events.filter(e => 
              e.event_type === 'api_invalid_key' || 
              e.event_type === 'api_auth_failed' || 
              e.event_type === 'api_rate_limit_exceeded' ||
              e.event_type === 'api_access_denied_ip'
            ).length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <span className="text-red-700 font-medium">
                    {events.filter(e => 
                      e.event_type === 'api_invalid_key' || 
                      e.event_type === 'api_auth_failed' || 
                      e.event_type === 'api_rate_limit_exceeded' ||
                      e.event_type === 'api_access_denied_ip'
                    ).length} unauthorized API access attempt(s) detected
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-2 font-semibold">Time</th>
                        <th className="text-left p-2 font-semibold">Type</th>
                        <th className="text-left p-2 font-semibold">IP Address</th>
                        <th className="text-left p-2 font-semibold">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.filter(e => 
                        e.event_type === 'api_invalid_key' || 
                        e.event_type === 'api_auth_failed' || 
                        e.event_type === 'api_rate_limit_exceeded' ||
                        e.event_type === 'api_access_denied_ip'
                      ).slice(0, 10).map((event, idx) => (
                        <tr key={idx} className="border-b hover:bg-red-50">
                          <td className="p-2">{new Date(event.timestamp).toLocaleString()}</td>
                          <td className="p-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              event.event_type === 'api_invalid_key' ? 'bg-red-100 text-red-700' :
                              event.event_type === 'api_rate_limit_exceeded' ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {event.event_type === 'api_invalid_key' ? 'Invalid Key' :
                               event.event_type === 'api_rate_limit_exceeded' ? 'Rate Limited' :
                               event.event_type === 'api_access_denied_ip' ? 'IP Denied' :
                               'Auth Failed'}
                            </span>
                          </td>
                          <td className="p-2 font-mono text-xs">{event.ip_address || 'N/A'}</td>
                          <td className="p-2 text-xs text-slate-600 max-w-xs truncate">{event.details}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Shield className="h-12 w-12 mx-auto mb-3 text-green-300" />
                <p className="text-sm text-green-600 font-medium">No unauthorized API access attempts</p>
                <p className="text-xs mt-1">All API requests have valid authentication</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* All Events Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Events</CardTitle>
          <CardDescription>Recent security events and user activity</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-semibold text-slate-700">Date</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Type</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Details</th>
                  <th className="text-left p-3 font-semibold text-slate-700">IP Address</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Location</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Status</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, index) => (
                  <tr key={index} className="border-b hover:bg-slate-50">
                    <td className="p-3 text-sm">{new Date(event.timestamp).toLocaleString()}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        event.event_type === 'login_success' ? 'bg-green-100 text-green-700' :
                        event.event_type === 'login_failed' ? 'bg-red-100 text-red-700' :
                        event.event_type === 'mfa_enabled' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {event.event_type}
                      </span>
                    </td>
                    <td className="p-3 text-sm">{event.details}</td>
                    <td className="p-3 text-sm font-mono">{event.ip_address || 'N/A'}</td>
                    <td className="p-3 text-sm">{event.user_email}</td>
                    <td className="p-3">
                      <span className={`w-2 h-2 rounded-full inline-block ${
                        event.severity === 'info' ? 'bg-blue-500' :
                        event.severity === 'low' ? 'bg-green-500' :
                        event.severity === 'medium' ? 'bg-yellow-500' :
                        event.severity === 'high' ? 'bg-orange-500' :
                        'bg-red-500'
                      }`}></span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default Security;
